/**
 * Resolver a mano un movimiento del extracto.
 *
 * UNA DECISIÓN CADA VEZ. Primero QUÉ es —de un inquilino, de un inmueble, un gasto ya apuntado,
 * nada— y solo entonces los campos de esa respuesta. Con todo a la vista a la vez, el panel era
 * un formulario de seis campos para contestar una pregunta de una palabra.
 *
 * «RECORDAR» ES LA OPCIÓN QUE AHORRA TRABAJO EL MES QUE VIENE. «OP.NET Alquiler Zabalbide H4»
 * no trae nombre: la aplicación no puede saber de quién es hasta que alguien se lo dice una vez.
 * El texto que se guarda se puede retocar, porque el que se propone es el del banco sin el mes,
 * y quien conoce a sus inquilinos sabe mejor qué parte es la que se repite.
 *
 * SI LA IA LO HA MIRADO, su duda va arriba, en sus palabras, y el panel llega con su propuesta
 * ya elegida: resolver una duda es leerla, comprobar lo que dice y pulsar Guardar (o cambiar lo
 * que no cuadre). Si lo resolvió ella, se dice por qué; «Volver a pendiente» lo deshace.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BankMatchMethod,
  BankMovement,
  BankMovementActionInput,
  LedgerBank,
  LedgerProperty,
  RememberPreview,
} from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import { Checkbox, Field, FormSection, Input, RadioGroup, Select } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents, formatShortDate } from '../../lib/format';
import styles from './Ledger.module.css';

type Modo = 'tenant' | 'property' | 'link' | 'ignore';

/** Cómo se decidió lo que hay ahora en un movimiento que la regla cambiaría. */
const COMO: Record<BankMatchMethod, string> = {
  regla: 'por una regla',
  cuenta: 'por la cuenta',
  nombre: 'por el nombre',
  unidad: 'por la habitación',
  importe: 'por el importe',
  ia: 'por la IA',
  manual: 'lo decidió una persona',
};

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`;
}

interface MovementDrawerProps {
  movement: BankMovement;
  bank: LedgerBank;
  properties: LedgerProperty[];
  onClose: () => void;
}

export function MovementDrawer({ movement, bank, properties, onClose }: MovementDrawerProps) {
  const queryClient = useQueryClient();
  const ingreso = movement.kind === 'ingreso';

  /** Los gastos del mes que todavía no tienen cargo del banco: los que este cargo puede pagar. */
  const gastosLibres = useMemo(() => {
    // Llegan ordenados del servidor: mismo importe y, primero, el mismo comercio y la fecha más cercana.
    const orden = new Map(movement.suggestions.expenseEntryIds.map((id, indice) => [id, indice]));
    const todos = properties.flatMap((property) =>
      property.entries
        .filter((entry) => !entry.bankMatch?.linked)
        .map((entry) => ({
          value: entry.id,
          label: `${orden.has(entry.id) ? '★ ' : ''}${property.name} · ${entry.concept} · ${formatEurCents(entry.amount)}`,
          sugerido: orden.has(entry.id),
        })),
    );
    // Los que encajan, arriba y en ese orden: casi siempre es el primero.
    return [
      ...todos.filter((g) => g.sugerido).sort((a, b) => orden.get(a.value)! - orden.get(b.value)!),
      ...todos.filter((g) => !g.sugerido),
    ];
  }, [movement, properties]);

  /*
   * Los candidatos llegan del servidor YA ORDENADOS de más a menos probable, cada uno con su
   * motivo. Van arriba con ★ y en ese orden; detrás, todos los demás por nombre.
   */
  const inquilinos = useMemo(() => {
    const orden = new Map(movement.suggestions.tenants.map((s, indice) => [s.id, indice]));
    const opciones = bank.tenantOptions.map((option) => ({
      value: option.id,
      label: `${orden.has(option.id) ? '★ ' : ''}${option.fullName} — ${option.label}`,
      sugerido: orden.has(option.id),
    }));
    return [
      ...opciones.filter((o) => o.sugerido).sort((a, b) => orden.get(a.value)! - orden.get(b.value)!),
      ...opciones.filter((o) => !o.sugerido),
    ];
  }, [movement, bank.tenantOptions]);
  const nombres = new Map(bank.tenantOptions.map((option) => [option.id, option.fullName]));

  /*
   * Los valores iniciales se toman al MONTAR: la página monta un panel por movimiento (`key`),
   * así que abrir otro arranca limpio sin un efecto que lo reinicie a destiempo.
   */
  const ia = movement.status === 'pendiente' ? movement.aiProposal : null;
  const [modo, setModo] = useState<Modo>(() => {
    if (ia) {
      if (ia.action === 'tenant' && ingreso) return 'tenant';
      if ((ia.action === 'property' || ia.action === 'recurring') && ia.propertyId) return 'property';
      if (ia.action === 'link' && gastosLibres.some((g) => g.value === ia.expenseEntryId)) return 'link';
      if (ia.action === 'ignore') return 'ignore';
    }
    return ingreso ? 'tenant' : gastosLibres.some((g) => g.sugerido) ? 'link' : 'property';
  });
  const [tenantId, setTenantId] = useState(
    () => movement.tenant?.id ?? ia?.tenantId ?? inquilinos.find((o) => o.sugerido)?.value ?? '',
  );
  /*
   * «Recordar» viene MARCADO: si la aplicación no supo de quién era, el mes que viene tampoco
   * lo sabrá, y resolverlo a mano otra vez es justo el trabajo que se quiere ahorrar. Se
   * desmarca para un pago excepcional que no se va a repetir.
   */
  const [recordar, setRecordar] = useState(Boolean(movement.suggestedPattern));
  const [patron, setPatron] = useState(movement.suggestedPattern ?? '');
  const [propertyId, setPropertyId] = useState(movement.property?.id ?? ia?.propertyId ?? '');
  const [concepto, setConcepto] = useState(ia?.concept ?? '');
  const [entryId, setEntryId] = useState(
    () =>
      (ia?.expenseEntryId && gastosLibres.some((g) => g.value === ia.expenseEntryId) ? ia.expenseEntryId : null) ??
      gastosLibres.find((g) => g.sugerido)?.value ??
      '',
  );
  const [nota, setNota] = useState(movement.notes ?? (ia?.action === 'ignore' ? ia.reason.slice(0, 500) : ''));
  /** El gasto fijo que propone la IA: va con el inmueble mientras no se cambie de inmueble. */
  const gastoFijo = ia?.action === 'recurring' && ia.propertyId === propertyId ? ia.recurringExpenseId : null;

  const guardar = useMutation({
    mutationFn: (body: BankMovementActionInput) => api.resolveMovement(movement.id, body),
    onSuccess: async (resultado, body) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['rent-charges'] });
      const otros = resultado?.alsoChanged ?? 0;
      toast.success(
        body.action === 'reset'
          ? 'El movimiento vuelve a estar pendiente.'
          : body.action === 'tenant' && body.remember
            ? otros > 0
              ? `Asignado, y también ${plural(otros, 'movimiento', 'movimientos')} de otros meses. Lo reconoceré solo a partir de ahora.`
              : 'Asignado. Lo reconoceré solo los próximos meses.'
            : 'Movimiento resuelto.',
      );
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido guardar.'),
  });

  /*
   * RECORDAR ENSEÑA ANTES LO QUE CAMBIARÍA EN OTROS MESES. Al guardar con «Recordarlo» se busca
   * qué otros ingresos reconocería la regla; si hay alguno, el panel pasa a la lista para elegir
   * cuáles cambian, y solo con esa confirmación se guarda. Sin ninguno, se guarda directamente.
   */
  const [confirmacion, setConfirmacion] = useState<RememberPreview | null>(null);
  const [elegidos, setElegidos] = useState<Set<string>>(() => new Set());
  const nombreInquilino = nombres.get(tenantId) ?? 'este inquilino';

  const guardarInquilino = (applyTo: string[]) =>
    guardar.mutate({
      action: 'tenant',
      tenantId,
      remember: recordar,
      ...(recordar && patron.trim() ? { pattern: patron.trim() } : {}),
      ...(recordar ? { applyTo } : {}),
    });

  const previsualizar = useMutation({
    mutationFn: () => api.rememberPreview(movement.id, { tenantId, ...(patron.trim() ? { pattern: patron.trim() } : {}) }),
    onSuccess: (preview) => {
      if (preview.matches.length === 0) {
        guardarInquilino([]);
        return;
      }
      setElegidos(new Set(preview.matches.filter((m) => m.suggested).map((m) => m.movementId)));
      setConfirmacion(preview);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido comprobar qué más cambiaría.'),
  });
  const ocupado = guardar.isPending || previsualizar.isPending;

  function alternar(movementId: string) {
    setElegidos((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(movementId)) nuevo.delete(movementId);
      else nuevo.add(movementId);
      return nuevo;
    });
  }

  function enviar() {
    if (modo === 'tenant') {
      if (recordar) previsualizar.mutate();
      else guardarInquilino([]);
    } else if (modo === 'property') {
      guardar.mutate({
        action: 'property',
        propertyId,
        concept: concepto.trim() || null,
        ...(gastoFijo ? { recurringExpenseId: gastoFijo } : {}),
      });
    } else if (modo === 'link') {
      guardar.mutate({ action: 'link', expenseEntryId: entryId });
    } else {
      guardar.mutate({ action: 'ignore', note: nota.trim() || null });
    }
  }

  const valido =
    modo === 'tenant'
      ? Boolean(tenantId) && (!recordar || patron.trim().length >= 4)
      : modo === 'property'
        ? Boolean(propertyId)
        : modo === 'link'
          ? Boolean(entryId)
          : true;

  const opcionesModo = ingreso
    ? [
        { value: 'tenant', title: 'Es de un inquilino', hint: 'Se imputa a su inmueble y se registra el cobro de su renta.' },
        { value: 'property', title: 'Es de un inmueble, sin inquilino', hint: 'Una devolución, un seguro, una fianza de alguien que aún no está dado de alta.' },
        { value: 'ignore', title: 'No es de ningún inmueble', hint: 'Un traspaso desde otra cuenta propia, una aportación de socios.' },
      ]
    : [
        ...(gastosLibres.length > 0
          ? [{ value: 'link', title: 'Paga un gasto ya apuntado', hint: 'El ticket ya está subido; este cargo es su pago.' }]
          : []),
        { value: 'property', title: 'Es un gasto de un inmueble', hint: 'Se apunta en el mes, sin factura; el ticket se adjunta después.' },
        { value: 'ignore', title: 'No es de ningún inmueble', hint: 'Un traspaso a otra cuenta propia, una comisión general.' },
      ];

  return (
    <Drawer
      open
      title={
        confirmacion ? 'Recordar también en otros meses' : ingreso ? '¿De quién es este ingreso?' : '¿Qué es este cargo?'
      }
      subtitle={`${formatShortDate(movement.bookedAt)} · ${formatEurCents(movement.amount)}`}
      onClose={onClose}
      footer={
        confirmacion ? (
          <div className={styles.pie}>
            <Button variant="secondary" size="sm" onClick={() => setConfirmacion(null)} disabled={ocupado}>
              Atrás
            </Button>
            <div className={styles.pieDerecha}>
              {elegidos.size > 0 && (
                <Button variant="secondary" size="sm" onClick={() => guardarInquilino([])} disabled={ocupado}>
                  Solo este
                </Button>
              )}
              <Button size="sm" onClick={() => guardarInquilino([...elegidos])} disabled={ocupado}>
                {guardar.isPending
                  ? 'Guardando…'
                  : elegidos.size > 0
                    ? `Guardar y cambiar ${elegidos.size}`
                    : 'Guardar solo este'}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.pie}>
            {movement.status !== 'pendiente' ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => guardar.mutate({ action: 'reset' })}
                disabled={ocupado}
              >
                Volver a pendiente
              </Button>
            ) : (
              <span />
            )}
            <div className={styles.pieDerecha}>
              <Button variant="secondary" size="sm" onClick={onClose} disabled={ocupado}>
                Cancelar
              </Button>
              <Button size="sm" onClick={enviar} disabled={!valido || ocupado}>
                {previsualizar.isPending ? 'Comprobando otros meses…' : guardar.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </div>
        )
      }
    >
      {confirmacion ? (
        <ConfirmarRecordar
          preview={confirmacion}
          nombre={nombreInquilino}
          elegidos={elegidos}
          onToggle={alternar}
          onTodos={(todos) =>
            setElegidos(new Set(todos ? confirmacion.matches.filter((m) => !m.locked).map((m) => m.movementId) : []))
          }
        />
      ) : (
        <>
          <FormSection title="En el extracto">
            <p className={styles.movementQuote}>{movement.description}</p>
          </FormSection>

          {movement.status === 'pendiente' && movement.aiDoubt && (
            <div className={styles.iaDuda} role="note">
              <span className={styles.iaDudaTag}>Duda de la IA</span>
              <p>{movement.aiDoubt}</p>
              {ia && <p className={styles.iaPropone}>Te dejo elegido lo que propone: compruébalo y guarda, o cámbialo.</p>}
            </div>
          )}
          {movement.status !== 'pendiente' && movement.matchMethod === 'ia' && (
            <div className={styles.iaHecho} role="note">
              <span className={styles.iaHechoTag}>Lo decidió la IA</span>
              <p>{movement.matchDetail ?? 'Sin explicación.'}</p>
              <p className={styles.iaPropone}>Si no es así, «Volver a pendiente» lo deshace entero.</p>
            </div>
          )}

          <FormSection>
            <RadioGroup
              name="modo"
              legend="Qué es"
              value={modo}
              options={opcionesModo}
              onChange={(value) => setModo(value as Modo)}
            />
          </FormSection>

          {modo === 'tenant' && (
            <FormSection
              title="Inquilino"
              hint={
                movement.suggestions.tenants.length > 0
                  ? 'Los marcados con ★ son los que podrían ser, de más a menos probable.'
                  : 'No he encontrado ninguna pista: ni nombre, ni habitación, ni importe.'
              }
            >
              <Field label="Inquilino" required>
                <Select
                  options={inquilinos}
                  placeholder="Elige el inquilino"
                  value={tenantId}
                  onChange={(event) => setTenantId(event.target.value)}
                />
              </Field>
              {/* Por qué se propone cada uno: elegir sabiendo el motivo, no a ciegas por la ★. */}
              {movement.suggestions.tenants.length > 0 && (
                <ul className={styles.reasons}>
                  {movement.suggestions.tenants.map((sugerencia) => (
                    <li key={sugerencia.id}>
                      <strong>{nombres.get(sugerencia.id) ?? 'Inquilino'}</strong>: {sugerencia.reason}
                    </li>
                  ))}
                </ul>
              )}
              <Checkbox
                label="Recordarlo para los próximos meses"
                hint="Cuando en el extracto aparezca este texto, se asignará solo a este inquilino. Si ya hay movimientos de otros meses con ese texto, antes de guardar te enseño cuáles cambiarían."
                checked={recordar}
                onChange={(event) => setRecordar(event.target.checked)}
              />
              {recordar && (
                <Field
                  label="Texto a reconocer"
                  required
                  help={
                    movement.patternIsGeneric
                      ? `Es un texto que podría escribir cualquiera: lo recordaré junto con el importe (${formatEurCents(movement.amount)}), para no confundirlo con los pagos de otros.`
                      : 'Lo que se repite cada mes. Sin el mes, que cambia.'
                  }
                >
                  <Input value={patron} onChange={(event) => setPatron(event.target.value)} maxLength={120} />
                </Field>
              )}
            </FormSection>
          )}

          {modo === 'property' && (
            <FormSection title="Inmueble">
              <Field label="Inmueble" required>
                <Select
                  options={properties.map((property) => ({ value: property.id, label: `${property.name} · ${property.city}` }))}
                  placeholder="Elige el inmueble"
                  value={propertyId}
                  onChange={(event) => setPropertyId(event.target.value)}
                />
              </Field>
              {gastoFijo && (
                <p className={styles.iaPropone}>
                  Cubre el gasto fijo «{ia?.concept}» de este inmueble: el hueco de este mes quedará cerrado.
                </p>
              )}
              {!ingreso && (
                <Field label="Concepto" optional help="Si lo dejas vacío, se usa el texto del banco.">
                  <Input
                    value={concepto}
                    onChange={(event) => setConcepto(event.target.value)}
                    placeholder="Comunidad"
                    maxLength={160}
                  />
                </Field>
              )}
            </FormSection>
          )}

          {modo === 'link' && (
            <FormSection title="Gasto" hint="Los marcados con ★ tienen exactamente este importe.">
              <Field label="Gasto apuntado" required>
                <Select
                  options={gastosLibres}
                  placeholder="Elige el gasto"
                  value={entryId}
                  onChange={(event) => setEntryId(event.target.value)}
                />
              </Field>
            </FormSection>
          )}

          {modo === 'ignore' && (
            <FormSection title="Nota">
              <Field label="Por qué no es de ningún inmueble" optional>
                <Input
                  value={nota}
                  onChange={(event) => setNota(event.target.value)}
                  placeholder="Traspaso a otra cuenta"
                  maxLength={500}
                />
              </Field>
            </FormSection>
          )}
        </>
      )}
    </Drawer>
  );
}

/**
 * La lista de lo que cambiaría en otros meses al recordar: cada ingreso que la regla reconoce,
 * con lo que es ahora y lo que pasará a ser, para marcar o desmarcar uno a uno. Lo de un mes
 * conciliado se ve, pero no se puede marcar.
 */
function ConfirmarRecordar({
  preview,
  nombre,
  elegidos,
  onToggle,
  onTodos,
}: {
  preview: RememberPreview;
  nombre: string;
  elegidos: Set<string>;
  onToggle: (movementId: string) => void;
  onTodos: (todos: boolean) => void;
}) {
  const libres = preview.matches.filter((m) => !m.locked);
  return (
    <>
      <FormSection title="Texto que se recordará">
        <p className={styles.movementQuote}>
          {preview.pattern}
          {preview.amount ? ` · solo por ${formatEurCents(preview.amount)}` : ''}
        </p>
      </FormSection>

      <FormSection
        title={`También lo reconoce en ${plural(preview.matches.length, 'movimiento', 'movimientos')} de otros meses`}
        hint={`Marca los que pasan a ser de ${nombre}: se deshace lo que tenían (cobros incluidos) y se imputan a su renta.${
          preview.unchanged > 0 ? ` Otros ${preview.unchanged} ya eran suyos.` : ''
        }`}
      >
        {libres.length > 1 && (
          <div className={styles.recordarTodos}>
            <button type="button" onClick={() => onTodos(true)}>
              Marcar todos
            </button>
            <button type="button" onClick={() => onTodos(false)}>
              Ninguno
            </button>
          </div>
        )}
        <ul className={styles.recordarLista}>
          {preview.matches.map((m) => (
            <li key={m.movementId} className={m.locked ? styles.recordarBloqueado : undefined}>
              <label className={styles.recordarFila}>
                <input
                  type="checkbox"
                  checked={elegidos.has(m.movementId)}
                  disabled={m.locked}
                  onChange={() => onToggle(m.movementId)}
                />
                <span className={styles.recordarCuerpo}>
                  <span className={styles.recordarLinea}>
                    <span>
                      {formatShortDate(m.bookedAt)} · {m.description}
                    </span>
                    <strong>{formatEurCents(m.amount)}</strong>
                  </span>
                  <span className={styles.recordarCambio}>
                    {m.locked ? (
                      <>Mes conciliado: reábrelo para cambiarlo. Ahora: {m.current}</>
                    ) : (
                      <>
                        {m.current}
                        {m.currentMethod ? ` (${COMO[m.currentMethod]})` : ''} → <strong>{nombre}</strong>
                      </>
                    )}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className={styles.iaPropone}>
          Lo que decidió una persona sale sin marcar. La regla se guarda igualmente: los extractos que subas a partir de
          ahora lo reconocerán solos.
        </p>
      </FormSection>
    </>
  );
}
